import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { gql } from '@apollo/client';
import toast from 'react-hot-toast';
import { XMarkIcon, UserCircleIcon, PlusIcon, TrashIcon, ArrowRightOnRectangleIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import {
  GET_USER_DETAIL_ADMIN,
  GET_USER_ROLES,
  GET_ALL_ROLES,
  ASSOCIATE_ROLE,
  DISSOCIATE_ROLE,
} from '../../graphql/queries';
import {
  SET_USER_STATUS_ADMIN,
  RESET_USER_PASSWORD_ADMIN,
} from '../../graphql/mutations';
import { Spinner } from '../common/Spinner';
import ChannelIcon from '../campaigns/ChannelIcon';
import { impersonation } from '../../services/impersonation';
import { graphqlApi } from '../../services/graphql';

export interface UserDetailModalProps {
  userId: string;
  onClose: () => void;
}

interface AdminUserDetail {
  userId: string;
  email: string;
  name?: string | null;
  profileImage?: string | null;
  userType?: string | null;
  status?: string | null;
  oauthProvider?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  lastLoginAt?: string | null;
  campaignCount: number;
  hostId?: string | null;
  influencerId?: string | null;
  withdrawnAt?: string | null;
  withdrawalReasons?: string[] | null;
  withdrawalFeedback?: string | null;
  social: {
    instagram?: SocialChannel | null;
    blog?: SocialChannel | null;
    youtube?: SocialChannel | null;
    tiktok?: SocialChannel | null;
    naverblog?: SocialChannel | null;
    twitter?: SocialChannel | null;
  };
}

interface SocialChannel {
  account?: string | null;
  followerCount?: number | null;
}

interface UserRole {
  roleId: string;
  roleName: string;
  description?: string | null;
  permissions?: string[] | null;
  isSystemRole?: boolean | null;
  isActive?: boolean | null;
}

const formatDateTime = (iso?: string | null) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '-';
  return d.toISOString().replace('T', ' ').slice(0, 16);
};

const userTypeBadgeClass = (ut?: string | null) => {
  switch (ut) {
    case 'influencer': return 'bg-purple-100 text-purple-700';
    case 'brand': return 'bg-blue-100 text-blue-700';
    case 'agency': return 'bg-teal-100 text-teal-700';
    default: return 'bg-gray-100 text-gray-600';
  }
};

/** Applied campaign (match enriched with its campaign info) for the list. */
interface AppliedCampaign {
  matchId: string;
  campaignId: string;
  campaignName: string;
  brandName?: string;
  status: string;
  selectedMissions?: string[];
  appliedAt?: string;
}

/** Match status → { Korean label, badge classes } matching the mockup palette. */
const MATCH_STATUS_META: Record<string, { labelKey: string; defaultLabel: string; className: string }> = {
  applied: { labelKey: 'admin:users.detail.matchStatus.applied', defaultLabel: '지원', className: 'bg-blue-50 text-blue-800' },
  shortlisted: { labelKey: 'admin:users.detail.matchStatus.shortlisted', defaultLabel: '1차 후보', className: 'bg-amber-50 text-amber-800' },
  requested: { labelKey: 'admin:users.detail.matchStatus.requested', defaultLabel: '진행요청', className: 'bg-indigo-50 text-indigo-700' },
  accepted: { labelKey: 'admin:users.detail.matchStatus.accepted', defaultLabel: '최종 선정', className: 'bg-green-50 text-green-700' },
  completed: { labelKey: 'admin:users.detail.matchStatus.completed', defaultLabel: '완료', className: 'bg-emerald-50 text-emerald-700' },
  rejected: { labelKey: 'admin:users.detail.matchStatus.rejected', defaultLabel: '거절', className: 'bg-red-50 text-red-700' },
  canceled: { labelKey: 'admin:users.detail.matchStatus.canceled', defaultLabel: '취소', className: 'bg-gray-100 text-gray-500' },
  expired: { labelKey: 'admin:users.detail.matchStatus.expired', defaultLabel: '만료', className: 'bg-gray-100 text-gray-500' },
};

const UserDetailModal: React.FC<UserDetailModalProps> = ({ userId, onClose }) => {
  const { t } = useTranslation(['admin', 'common']);
  const navigate = useNavigate();
  const [showRolePicker, setShowRolePicker] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  const [appliedCampaigns, setAppliedCampaigns] = useState<AppliedCampaign[]>([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [campaignsExpanded, setCampaignsExpanded] = useState(true);

  const { data, loading, error, refetch } = useQuery<{ getUserDetailAdmin: AdminUserDetail }>(
    gql`${GET_USER_DETAIL_ADMIN}`,
    {
      variables: { userId },
      fetchPolicy: 'network-only',
    },
  );

  const {
    data: userRolesData,
    loading: userRolesLoading,
    refetch: refetchUserRoles,
  } = useQuery<{ getUserRoles: { roles: UserRole[]; count: number } }>(
    GET_USER_ROLES,
    { variables: { userId }, fetchPolicy: 'network-only' },
  );
  const currentRoles: UserRole[] = userRolesData?.getUserRoles?.roles ?? [];

  const [setStatus, { loading: statusSaving }] = useMutation(gql`${SET_USER_STATUS_ADMIN}`);
  const [resetPassword, { loading: resetting }] = useMutation(gql`${RESET_USER_PASSWORD_ADMIN}`);
  const [dissociateRole, { loading: dissociating }] = useMutation(DISSOCIATE_ROLE);

  const user = data?.getUserDetailAdmin;
  const isSuspended = user?.status === 'suspended';

  // For influencer users, load the campaigns they've applied to (matches),
  // enriched with each campaign's name/brand for the 참여 캠페인 list.
  const influencerId = user?.influencerId;
  const isInfluencer = user?.userType === 'influencer';
  useEffect(() => {
    if (!isInfluencer || !influencerId) {
      setAppliedCampaigns([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingCampaigns(true);
      try {
        const [matchRes, campRes] = await Promise.all([
          graphqlApi.getMatches({ influencerId }),
          graphqlApi.getCampaigns({}),
        ]);
        const matches: any[] = matchRes?.success ? matchRes.data?.matches || [] : [];
        const campaigns: any[] = campRes?.success ? campRes.data?.campaigns || [] : [];
        const campaignMap: Record<string, any> = {};
        for (const c of campaigns) campaignMap[c.campaignId] = c;

        const rows: AppliedCampaign[] = matches
          .map((m) => {
            const c = campaignMap[m.campaignId];
            return {
              matchId: m.matchId,
              campaignId: m.campaignId,
              campaignName:
                c?.campaignName || c?.title || t('admin:users.detail.deletedCampaign', '(삭제된 캠페인)'),
              brandName: c?.brandName || '',
              status: m.status,
              selectedMissions: m.selectedMissions,
              appliedAt: m.appliedAt || m.createdAt,
            };
          })
          .sort((a, b) => (b.appliedAt || '').localeCompare(a.appliedAt || ''));

        if (!cancelled) setAppliedCampaigns(rows);
      } catch {
        if (!cancelled) setAppliedCampaigns([]);
      } finally {
        if (!cancelled) setLoadingCampaigns(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isInfluencer, influencerId, t]);

  const goToCampaignApplicants = (campaignId: string) => {
    onClose();
    navigate(`/campaigns/${campaignId}/applicants`);
  };

  const fmtAppliedDate = (iso?: string) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return Number.isFinite(d.getTime())
      ? d.toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' })
      : '-';
  };

  const snsRows = useMemo(() => {
    const s = user?.social ?? {};
    return [
      { key: 'instagram', label: 'Instagram', channel: s.instagram ?? null },
      { key: 'blog', label: t('admin:users.social.blog', 'Blog'), channel: s.blog ?? null },
      { key: 'youtube', label: 'YouTube', channel: s.youtube ?? null },
    ];
  }, [user?.social, t]);

  const formatFollowers = (n?: number | null) => {
    if (typeof n !== 'number' || !Number.isFinite(n)) return null;
    return n.toLocaleString();
  };

  /**
   * Turn a stored SNS account value into an absolute, clickable URL.
   * Accounts are usually stored as full URLs, but can be bare handles
   * (e.g. "@me.you_travel"). Returns null when we can't build a sane link.
   */
  const buildSocialHref = (channelKey: string, account?: string | null): string | null => {
    if (!account) return null;
    const raw = account.trim();
    if (!raw) return null;
    if (/^https?:\/\//i.test(raw)) return raw;
    if (raw.startsWith('www.')) return `https://${raw}`;
    const handle = raw.replace(/^@/, '');
    switch (channelKey) {
      case 'instagram':
        return `https://instagram.com/${handle}`;
      case 'youtube':
        return `https://youtube.com/@${handle}`;
      case 'tiktok':
        return `https://tiktok.com/@${handle}`;
      case 'twitter':
        return `https://twitter.com/${handle}`;
      case 'blog':
      case 'naverblog':
        // Naver blog handles map to blog.naver.com/<id>; anything else with a
        // domain-looking value gets a protocol prefix, otherwise leave as text.
        if (/\./.test(raw)) return `https://${raw}`;
        return `https://blog.naver.com/${handle}`;
      default:
        return /\./.test(raw) ? `https://${raw}` : null;
    }
  };

  const handleToggleStatus = async () => {
    if (!user) return;
    const nextStatus = isSuspended ? 'active' : 'suspended';
    const confirmMsg = isSuspended
      ? t('admin:users.detail.confirmActivate', '이 계정을 활성화하시겠습니까?')
      : t('admin:users.detail.confirmSuspend', '이 계정을 정지하시겠습니까?');
    if (!window.confirm(confirmMsg)) return;

    try {
      const res = await setStatus({
        variables: { input: { userId: user.userId, status: nextStatus } },
      });
      if (res.data?.setUserStatusAdmin?.success) {
        toast.success(res.data.setUserStatusAdmin.message || t('admin:users.detail.statusUpdated', '상태가 변경되었습니다'));
        await refetch();
      } else {
        toast.error(t('admin:users.detail.statusUpdateFailed', '상태 변경에 실패했습니다'));
      }
    } catch (err: any) {
      toast.error(err?.message || t('admin:users.detail.statusUpdateFailed', '상태 변경에 실패했습니다'));
    }
  };

  const handleResetPassword = async () => {
    if (!user) return;
    const confirmMsg = t(
      'admin:users.detail.confirmResetPassword',
      '해당 사용자에게 비밀번호 재설정 이메일을 보냅니다. 계속하시겠습니까?',
    );
    if (!window.confirm(confirmMsg)) return;

    try {
      const res = await resetPassword({
        variables: { input: { userId: user.userId } },
      });
      if (res.data?.resetUserPasswordAdmin?.success) {
        toast.success(res.data.resetUserPasswordAdmin.message || t('admin:users.detail.passwordResetSent', '비밀번호 재설정 이메일을 보냈습니다'));
      } else {
        toast.error(t('admin:users.detail.passwordResetFailed', '비밀번호 초기화에 실패했습니다'));
      }
    } catch (err: any) {
      toast.error(err?.message || t('admin:users.detail.passwordResetFailed', '비밀번호 초기화에 실패했습니다'));
    }
  };

  const handleImpersonate = async () => {
    if (!user) return;
    // Admin-on-admin is blocked server-side; guard early in the UI so we don't
    // surface a useless round-trip error.
    const targetIsAdmin = currentRoles.some((r) => r.roleName === 'admin');
    if (targetIsAdmin) {
      toast.error(
        t('admin:impersonation.cannotImpersonateAdmin', '관리자 계정으로는 대리 로그인할 수 없습니다'),
      );
      return;
    }
    if (user.status === 'suspended') {
      toast.error(
        t('admin:impersonation.cannotImpersonateSuspended', '정지된 계정으로는 대리 로그인할 수 없습니다'),
      );
      return;
    }

    const confirmMsg = t(
      'admin:impersonation.confirmStart',
      '{{name}} 계정으로 대리 로그인합니다. 모든 대리 로그인은 감사 로그에 기록됩니다. 종료하려면 상단 배너의 "원래 계정으로 돌아가기"를 눌러 로그아웃한 뒤 다시 로그인하세요. 계속하시겠습니까?',
      { name: user.name || user.email },
    );
    if (!window.confirm(confirmMsg)) return;

    setImpersonating(true);
    try {
      // startImpersonation hard-reloads to /dashboard on success. Nothing below
      // this line runs on the happy path.
      await impersonation.startImpersonation(user.userId, user.email);
    } catch (err: any) {
      setImpersonating(false);
      toast.error(
        err?.message || t('admin:impersonation.startFailed', '대리 로그인에 실패했습니다'),
      );
    }
  };

  const handleDissociate = async (role: UserRole) => {
    if (!user) return;
    if (currentRoles.length <= 1) {
      toast.error(t('admin:users.detail.cannotRemoveLastRole', '마지막 역할은 제거할 수 없습니다'));
      return;
    }
    if (
      !window.confirm(
        t('admin:users.detail.confirmDissociate', '{{role}} 역할을 해제하시겠습니까?', { role: role.roleName }),
      )
    ) return;

    try {
      await dissociateRole({
        variables: {
          input: {
            userId: user.userId,
            roleId: role.roleId,
            reason: 'Removed via admin user-detail modal',
          },
        },
      });
      toast.success(
        t('admin:users.detail.dissociateSuccess', '{{role}} 역할이 해제되었습니다', { role: role.roleName }),
      );
      await refetchUserRoles();
    } catch (err: any) {
      toast.error(err?.message || t('admin:users.detail.dissociateFailed', '역할 해제에 실패했습니다'));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">
            {t('admin:users.detail.title', '사용자 상세')}
          </h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="py-20 flex justify-center">
            <Spinner size="medium" />
          </div>
        ) : error || !user ? (
          <div className="px-6 py-10 text-center text-sm text-red-600">
            {error?.message || t('admin:users.detail.notFound', '사용자를 찾을 수 없습니다')}
          </div>
        ) : (
          <div className="px-6 py-5 space-y-5">
            {/* Header: profile + name + userType */}
            <div className="flex items-center gap-4">
              {user.profileImage ? (
                <img
                  src={user.profileImage}
                  alt={user.name || user.email}
                  className="w-16 h-16 rounded-full object-cover"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
                  <UserCircleIcon className="w-10 h-10 text-gray-400" />
                </div>
              )}
              <div className="flex-1">
                <div className="text-lg font-bold text-gray-900">{user.name || '-'}</div>
                <div className="mt-1">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${userTypeBadgeClass(user.userType)}`}>
                    {user.userType ? t(`admin:users.types.${user.userType}`, user.userType) : '-'}
                  </span>
                </div>
              </div>
            </div>

            <hr className="border-gray-200" />

            {/* Basic info */}
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">{t('admin:users.table.email')}</dt>
                <dd className="text-gray-900 font-medium break-all">{user.email}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">{t('admin:users.table.joinedAt')}</dt>
                <dd className="text-gray-900">{formatDateTime(user.createdAt)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">{t('admin:users.detail.lastLoginAt', '최근 로그인')}</dt>
                <dd className="text-gray-900">{formatDateTime(user.lastLoginAt)}</dd>
              </div>
            </dl>

            {/* Withdrawal info — shown for withdrawn accounts */}
            {(user.status === 'withdrawn' || user.withdrawnAt) && (
              <>
                <hr className="border-gray-200" />
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-2.5">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-700">
                      {t('admin:users.detail.withdrawal', '탈퇴 정보')}
                    </span>
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-200 text-gray-600 font-medium">
                      {t('admin:users.status.withdrawn', '탈퇴')}
                    </span>
                  </div>
                  {user.withdrawnAt && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">{t('admin:users.detail.withdrawnAt', '탈퇴일')}</span>
                      <span className="text-gray-900">{formatDateTime(user.withdrawnAt)}</span>
                    </div>
                  )}
                  <div className="text-sm">
                    <div className="text-gray-500 mb-1">{t('admin:users.detail.withdrawalReasons', '탈퇴 사유')}</div>
                    {(user.withdrawalReasons || []).length > 0 ? (
                      <ul className="list-disc list-inside space-y-0.5 text-gray-800">
                        {(user.withdrawalReasons || []).map((r, i) => (
                          <li key={i}>{r}</li>
                        ))}
                      </ul>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </div>
                  <div className="text-sm">
                    <div className="text-gray-500 mb-1">{t('admin:users.detail.withdrawalFeedback', '의견')}</div>
                    {user.withdrawalFeedback ? (
                      <p className="text-gray-800 whitespace-pre-wrap break-words">{user.withdrawalFeedback}</p>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </div>
                </div>
              </>
            )}

            <hr className="border-gray-200" />

            {/* Roles */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-700">
                  {t('admin:users.detail.roles', '역할')}
                </h3>
                <button
                  type="button"
                  onClick={() => setShowRolePicker(true)}
                  className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                >
                  <PlusIcon className="w-3.5 h-3.5" />
                  {t('admin:userRoles.addRoleBtn', '권한 추가')}
                </button>
              </div>
              {userRolesLoading ? (
                <div className="py-2 text-xs text-gray-400">{t('common:loading', '불러오는 중...')}</div>
              ) : currentRoles.length === 0 ? (
                <div className="py-2 text-xs text-gray-400">
                  {t('admin:users.detail.noRoles', '할당된 역할이 없습니다')}
                </div>
              ) : (
                <ul className="space-y-1.5">
                  {currentRoles.map((role) => (
                    <li
                      key={role.roleId}
                      className="flex items-center justify-between px-3 py-2 rounded-md border border-gray-200 bg-gray-50"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900">{role.roleName}</div>
                        {role.description && (
                          <div className="text-xs text-gray-500 truncate">{role.description}</div>
                        )}
                      </div>
                      <button
                        type="button"
                        disabled={dissociating || currentRoles.length <= 1}
                        onClick={() => handleDissociate(role)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md disabled:opacity-40 disabled:cursor-not-allowed"
                        title={
                          currentRoles.length <= 1
                            ? t('admin:users.detail.cannotRemoveLastRole', '마지막 역할은 제거할 수 없습니다')
                            : t('admin:users.detail.dissociate', '역할 해제')
                        }
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <hr className="border-gray-200" />

            {/* SNS */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                {t('admin:users.detail.sns', 'SNS 연동')}
              </h3>
              <ul className="space-y-1.5 text-sm">
                {snsRows.map((row) => {
                  const account = row.channel?.account || null;
                  const followers = formatFollowers(row.channel?.followerCount);
                  const href = buildSocialHref(row.key, account);
                  return (
                    <li key={row.key} className="flex items-center gap-2">
                      <ChannelIcon channel={row.key} className="w-4 h-4 flex-shrink-0" />
                      <span className="text-gray-500 w-20">{row.label}</span>
                      {account && href ? (
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 hover:underline flex-1 break-all"
                        >
                          {account}
                        </a>
                      ) : (
                        <span className="text-gray-900 flex-1 break-all">{account || '-'}</span>
                      )}
                      {followers !== null && (
                        <span className="text-xs text-gray-500 flex-shrink-0">
                          {t('admin:users.detail.followers', '{{count}} 팔로워', { count: row.channel?.followerCount ?? 0 })}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Campaigns */}
            {isInfluencer ? (
              <div>
                <button
                  type="button"
                  onClick={() => setCampaignsExpanded((v) => !v)}
                  className="w-full flex items-center justify-between"
                >
                  <span className="flex items-center gap-2 text-sm">
                    <span className="text-gray-500">
                      {t('admin:users.detail.campaignCount', '참여 캠페인')}
                    </span>
                    <span className="font-semibold text-gray-900">
                      {loadingCampaigns
                        ? '…'
                        : t('admin:users.detail.campaignCountValue', '{{count}}개', {
                            count: appliedCampaigns.length,
                          })}
                    </span>
                  </span>
                  {campaignsExpanded ? (
                    <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                  ) : (
                    <ChevronRightIcon className="w-4 h-4 text-gray-400" />
                  )}
                </button>

                {campaignsExpanded && (
                  <div className="mt-3 space-y-2">
                    {loadingCampaigns ? (
                      <div className="py-4 flex justify-center">
                        <Spinner size="small" />
                      </div>
                    ) : appliedCampaigns.length === 0 ? (
                      <p className="py-3 text-xs text-gray-400 text-center">
                        {t('admin:users.detail.noCampaigns', '참여한 캠페인이 없습니다')}
                      </p>
                    ) : (
                      appliedCampaigns.map((ac) => {
                        const meta = MATCH_STATUS_META[ac.status] || {
                          labelKey: '',
                          defaultLabel: ac.status,
                          className: 'bg-gray-100 text-gray-600',
                        };
                        const missions = ac.selectedMissions || [];
                        return (
                          <button
                            key={ac.matchId}
                            type="button"
                            onClick={() => goToCampaignApplicants(ac.campaignId)}
                            className="w-full text-left border border-gray-200 rounded-lg px-3 py-2.5 hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {ac.campaignName}
                                </p>
                                {ac.brandName && (
                                  <p className="mt-0.5 text-[11px] text-gray-400 truncate">
                                    {ac.brandName}
                                  </p>
                                )}
                              </div>
                              <span
                                className={`flex-shrink-0 text-[11px] px-2 py-0.5 rounded-full font-medium ${meta.className}`}
                              >
                                {meta.labelKey ? t(meta.labelKey, meta.defaultLabel) : ac.status}
                              </span>
                            </div>
                            <div className="flex items-center justify-between mt-2">
                              <span className="flex items-center gap-1">
                                {missions.length > 0 ? (
                                  missions.map((m) => (
                                    <ChannelIcon
                                      key={m}
                                      channel={m === 'naverblog' ? 'blog' : m}
                                      className="w-3.5 h-3.5"
                                    />
                                  ))
                                ) : (
                                  <span className="text-[11px] text-gray-300">-</span>
                                )}
                              </span>
                              <span className="flex items-center gap-1 text-[11px] text-gray-400">
                                {t('admin:users.detail.appliedOn', '지원일')} {fmtAppliedDate(ac.appliedAt)}
                                <ChevronRightIcon className="w-3.5 h-3.5 text-blue-500" />
                              </span>
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-500">
                  {t('admin:users.detail.campaignCount', '참여 캠페인')}
                </span>
                <span className="text-base font-semibold text-gray-900">
                  {t('admin:users.count', { count: user.campaignCount })}
                </span>
              </div>
            )}

            <hr className="border-gray-200" />

            {/* Account status actions */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                {t('admin:users.detail.accountStatus', '계정 상태')}
              </h3>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={statusSaving}
                  onClick={handleToggleStatus}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 ${
                    isSuspended
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-red-600 text-white hover:bg-red-700'
                  }`}
                >
                  {statusSaving
                    ? t('common:saving', '저장 중...')
                    : isSuspended
                      ? t('admin:users.detail.activate', '계정 활성화')
                      : t('admin:users.detail.suspend', '계정 정지')}
                </button>

                <button
                  type="button"
                  disabled={resetting}
                  onClick={handleResetPassword}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  {resetting
                    ? t('admin:users.detail.resettingPassword', '처리 중...')
                    : t('admin:users.detail.resetPassword', '비밀번호 초기화')}
                </button>

                <button
                  type="button"
                  disabled={impersonating || isSuspended}
                  onClick={handleImpersonate}
                  title={
                    isSuspended
                      ? t(
                          'admin:impersonation.cannotImpersonateSuspended',
                          '정지된 계정으로는 대리 로그인할 수 없습니다',
                        )
                      : t('admin:impersonation.loginAsUserTitle', '이 사용자 계정으로 로그인')
                  }
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {impersonating ? (
                    <Spinner size="small" />
                  ) : (
                    <ArrowRightOnRectangleIcon className="w-4 h-4" />
                  )}
                  {impersonating
                    ? t('admin:impersonation.starting', '로그인 중...')
                    : t('admin:impersonation.loginAsUser', '이 사용자로 로그인')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {showRolePicker && user && (
        <RolePickerModal
          userId={user.userId}
          currentRoleIds={currentRoles.map((r) => r.roleId)}
          onClose={() => setShowRolePicker(false)}
          onAssociated={async () => {
            setShowRolePicker(false);
            await refetchUserRoles();
          }}
        />
      )}
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────────────
 * Role picker — pick one existing role + enter reason, then associate.
 * Rendered inline here so callers don't need a second import.
 * ────────────────────────────────────────────────────────────────────── */
interface RolePickerModalProps {
  userId: string;
  currentRoleIds: string[];
  onClose: () => void;
  onAssociated: () => void;
}

const RolePickerModal: React.FC<RolePickerModalProps> = ({
  userId,
  currentRoleIds,
  onClose,
  onAssociated,
}) => {
  const { t } = useTranslation(['admin', 'common']);
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const [reason, setReason] = useState('');

  const { data, loading } = useQuery<{ getAllRoles: { roles: UserRole[]; count: number } }>(
    GET_ALL_ROLES,
    { variables: { includeInactive: false }, fetchPolicy: 'network-only' },
  );
  const allRoles: UserRole[] = data?.getAllRoles?.roles ?? [];
  const assignableRoles = allRoles.filter((r) => !currentRoleIds.includes(r.roleId));

  const [associateRole, { loading: saving }] = useMutation(ASSOCIATE_ROLE);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRoleId) {
      toast.error(t('admin:users.detail.selectRole', '역할을 선택하세요'));
      return;
    }
    const trimmedReason = reason.trim() || 'Assigned via admin user-detail modal';
    try {
      await associateRole({
        variables: {
          input: {
            userId,
            roleId: selectedRoleId,
            reason: trimmedReason,
          },
        },
      });
      toast.success(t('admin:users.detail.associateSuccess', '역할이 할당되었습니다'));
      onAssociated();
    } catch (err: any) {
      toast.error(err?.message || t('admin:users.detail.associateFailed', '역할 할당에 실패했습니다'));
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">
            {t('admin:users.detail.assignRoleTitle', '역할 할당')}
          </h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('admin:users.detail.roleLabel', '역할')} <span className="text-red-500">*</span>
            </label>
            {loading ? (
              <div className="py-2 text-xs text-gray-400">
                {t('common:loading', '불러오는 중...')}
              </div>
            ) : assignableRoles.length === 0 ? (
              <div className="px-3 py-2 text-xs text-gray-500 bg-gray-50 rounded-md border border-gray-200">
                {t('admin:users.detail.noAssignableRoles', '할당할 수 있는 역할이 없습니다')}
              </div>
            ) : (
              <select
                value={selectedRoleId}
                onChange={(e) => setSelectedRoleId(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:outline-none"
              >
                <option value="">
                  {t('admin:users.detail.rolePlaceholder', '역할을 선택하세요')}
                </option>
                {assignableRoles.map((role) => (
                  <option key={role.roleId} value={role.roleId}>
                    {role.roleName}
                    {role.description ? ` — ${role.description}` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('admin:users.detail.reasonLabel', '사유 (선택)')}
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('admin:users.detail.reasonPlaceholder', '감사 로그에 기록될 메모')}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:outline-none"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {t('common:cancel', '취소')}
            </button>
            <button
              type="submit"
              disabled={saving || !selectedRoleId}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {saving && <Spinner size="small" />}
              {t('admin:userRoles.addRoleBtn', '권한 추가')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserDetailModal;
